import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const TIMEZONES = [
  { value: 'America/Vancouver',  label: 'Pacific — Vancouver / Victoria' },
  { value: 'America/Edmonton',   label: 'Mountain — Edmonton / Calgary' },
  { value: 'America/Winnipeg',   label: 'Central — Winnipeg' },
  { value: 'America/Toronto',    label: 'Eastern — Toronto / Ottawa' },
  { value: 'America/Halifax',    label: 'Atlantic — Halifax' },
  { value: 'America/St_Johns',   label: "Newfoundland — St. John's" },
];

function tzFromCampus(campus) {
  const c = (campus || '').toLowerCase();
  if (c.includes('van') || c.includes('bc')) return 'America/Vancouver';
  if (c.includes('cal') || c.includes('alb') || c.includes('edm')) return 'America/Edmonton';
  if (c.includes('tor') || c.includes('ont')) return 'America/Toronto';
  return 'America/Edmonton';
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function todayStr() { return new Date().toISOString().slice(0, 10); }

// Convert a wall-clock date+time in a given IANA timezone to a UTC ISO instant.
function zonedToUtcISO(dateStr, timeStr, tz) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  const guessUTC = Date.UTC(y, m - 1, d, hh, mm);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(new Date(guessUTC)).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  let hour = parts.hour === '24' ? 0 : +parts.hour;
  const asZoned = Date.UTC(+parts.year, +parts.month - 1, +parts.day, hour, +parts.minute, +parts.second);
  const offset = asZoned - guessUTC;
  return new Date(guessUTC - offset).toISOString();
}

function fmtSlotPreview(dateStr, timeStr) {
  if (!dateStr || !timeStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  const dt = new Date(y, m - 1, d, hh, mm);
  return dt.toLocaleString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5 };

export default function SetUpInterviewModal({ student, onClose, onSuccess }) {
  const today = todayStr();
  const [meetingMode, setMeetingMode] = useState('in_person');
  const [location, setLocation] = useState('');
  const [timezone, setTimezone] = useState(tzFromCampus(student.campus));
  const [slotMinutes, setSlotMinutes] = useState(30);
  const [slots, setSlots] = useState([{ date: addDays(today, 1), time: '09:00' }]);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Prefill the in-person location + interview contact from the host's profile
  useEffect(() => {
    if (!student.host_id) return;
    api.get(`/hosts/${student.host_id}`).then(res => {
      const h = res.data;
      const addr = [h.address, h.city, h.province].filter(Boolean).join(', ');
      if (addr) setLocation(prev => prev || addr);
      const name = [h.contact_first_name, h.contact_last_name].filter(Boolean).join(' ');
      setContactName(prev => prev || name || '');
      setContactPhone(prev => prev || h.contact_phone || '');
      setContactEmail(prev => prev || h.contact_email || '');
    }).catch(() => {});
  }, [student.host_id]);

  const setSlot = (i, key, val) => setSlots(s => s.map((row, idx) => idx === i ? { ...row, [key]: val } : row));
  const addSlot = () => setSlots(s => [...s, { date: s[s.length - 1]?.date || addDays(today, 1), time: '11:00' }]);
  const removeSlot = (i) => setSlots(s => s.length > 1 ? s.filter((_, idx) => idx !== i) : s);

  const validSlots = slots.filter(s => s.date && s.time);
  const isValid = validSlots.length >= 1 && (meetingMode === 'online' || true);

  async function handleSubmit() {
    if (!isValid) { setError('Add at least one interview time.'); return; }
    setBusy(true); setError('');
    try {
      const isoSlots = validSlots
        .map(s => zonedToUtcISO(s.date, s.time, timezone))
        .filter(Boolean);
      // de-dupe identical instants
      const uniq = [...new Set(isoSlots)];
      await api.post(`/students/${student.id}/interview-session`, {
        slots: uniq,
        meeting_mode: meetingMode,
        location: meetingMode === 'in_person' ? location : null,
        timezone,
        slot_minutes: parseInt(slotMinutes),
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set up the interview.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="im-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="im-modal" style={{ maxWidth: 560 }}>
        <div className="im-header">
          <div>
            <div className="im-header-label">Placement Workflow</div>
            <h2 className="im-header-title">Schedule Interview</h2>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
              Enter the times <strong>{student.host_name || 'the host'}</strong> offered. Only <strong>{student.first_name}</strong> gets a link to pick one.
            </div>
          </div>
          <button className="im-close" onClick={onClose}>✕</button>
        </div>

        <div className="im-body" style={{ gap: 16 }}>
          {error && (
            <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Meeting mode */}
          <div>
            <label style={labelStyle}>Meeting type</label>
            <div className="iv-mode-toggle">
              <button type="button" className={meetingMode === 'in_person' ? 'active' : ''} onClick={() => setMeetingMode('in_person')}>
                <PinIcon /> In person
              </button>
              <button type="button" className={meetingMode === 'online' ? 'active' : ''} onClick={() => setMeetingMode('online')}>
                <VideoIcon /> Online
              </button>
            </div>
          </div>

          {meetingMode === 'in_person' ? (
            <div>
              <label style={labelStyle}>Location (editable — defaults to host address)</label>
              <textarea className="input" rows={2} value={location} onChange={e => setLocation(e.target.value)}
                placeholder="e.g. 225 11 Ave NW, Calgary, AB" style={{ resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', background: 'var(--bg-sunken)', padding: '10px 12px', borderRadius: 8 }}>
              <VideoIcon /> A Google Meet link will be generated automatically and added to the calendar invite for both the student and the host.
            </div>
          )}

          {/* Offered slots */}
          <div>
            <label style={labelStyle}>Interview times the host offered</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {slots.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="date" className="input" min={today} value={s.date} onChange={e => setSlot(i, 'date', e.target.value)} style={{ flex: 1 }} />
                  <input type="time" className="input" value={s.time} onChange={e => setSlot(i, 'time', e.target.value)} style={{ width: 120 }} />
                  <button type="button" className="iv-slot-x" onClick={() => removeSlot(i)} disabled={slots.length === 1} title="Remove">✕</button>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={addSlot}>+ Add another time</button>
          </div>

          {/* Timezone + duration */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Timezone</label>
              <select className="select" value={timezone} onChange={e => setTimezone(e.target.value)}>
                {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Duration</label>
              <select className="select" value={slotMinutes} onChange={e => setSlotMinutes(e.target.value)}>
                <option value={30}>30 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>
          </div>

          {/* Interview contact — prefilled from host, editable */}
          <div>
            <label style={labelStyle}>Interview contact (shown to the student — defaults to the host contact)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input className="input" placeholder="Contact name" value={contactName} onChange={e => setContactName(e.target.value)} style={{ gridColumn: '1 / -1' }} />
              <input className="input" placeholder="Phone" value={contactPhone} onChange={e => setContactPhone(e.target.value)} />
              <input className="input" type="email" placeholder="Email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
            </div>
          </div>

          {/* Preview */}
          {validSlots.length > 0 && (
            <div style={{ background: 'var(--bg-sunken)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>
                {student.first_name} will choose one of:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {validSlots.map((s, i) => (
                  <span key={i} style={{ fontSize: 12, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6, padding: '3px 8px' }}>
                    {fmtSlotPreview(s.date, s.time)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={!isValid || busy}>
            {busy ? 'Sending…' : 'Send Link to Student'}
          </button>
        </div>
      </div>
    </div>
  );
}

const PinIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
const VideoIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 4 }}><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>;
